import { UserRole, RoleConfig, RolePermission } from '../types';
import { getGenderedUserRole } from '../constants/medicalTerminology';

// Role configuration with Greek labels and icons
export const ROLE_CONFIGS: Record<UserRole, RoleConfig> = {
  member: {
    key: 'member',
    label: 'Μέλος',
    icon: '🩺',
    description: 'Παθολογοανατόμος μέλος της κοινότητας',
    hierarchy: 1,
    permissions: ['view_cases', 'create_cases', 'comment', 'vote'],
    cssClass: 'bg-blue-100 text-blue-800'
  },
  honorary_member: {
    key: 'honorary_member',
    label: 'Επίτιμο Μέλος',
    icon: '🩺✨',
    description: 'Έμπειρος παθολογοανατόμος με ειδική εμπειρία',
    hierarchy: 2,
    permissions: ['view_cases', 'create_cases', 'comment', 'vote', 'priority_response'],
    cssClass: 'bg-purple-100 text-purple-800'
  },
  moderator: {
    key: 'moderator',
    label: 'Συντονιστής',
    icon: '💉🔍',
    description: 'Συντονιστής φόρουμ και διαχειριστής περιστατικών',
    hierarchy: 3,
    permissions: ['view_cases', 'create_cases', 'comment', 'vote', 'moderate_forum', 'resolve_cases', 'delete_comments'],
    cssClass: 'bg-orange-100 text-orange-800'
  },
  admin: {
    key: 'admin',
    label: 'Διαχειριστής',
    icon: '🧬⚙️',
    description: 'Διαχειριστής συστήματος με πλήρη πρόσβαση',
    hierarchy: 4,
    permissions: ['all'],
    cssClass: 'bg-red-100 text-red-800'
  }
};

// Get role configuration
export const getRoleConfig = (role: UserRole): RoleConfig => {
  return ROLE_CONFIGS[role] || ROLE_CONFIGS.member;
};

// Get role display text with icon
export const getRoleDisplay = (role: UserRole): string => {
  const config = getRoleConfig(role);
  return `${config.icon} ${config.label}`;
};

// Get role permissions
export const getRolePermissions = (role: UserRole): RolePermission => {
  const config = getRoleConfig(role);
  
  return {
    canModerate: config.permissions.includes('moderate_forum') || config.permissions.includes('all'),
    canResolveCases: config.permissions.includes('resolve_cases') || config.permissions.includes('all'),
    canDeleteComments: config.permissions.includes('delete_comments') || config.permissions.includes('all'),
    canBanUsers: config.permissions.includes('ban_users') || config.permissions.includes('all'),
    canAccessAdmin: config.permissions.includes('all')
  };
};

// Check if user has specific permission
export const hasPermission = (userRole: UserRole, permission: string): boolean => {
  const config = getRoleConfig(userRole);
  return config.permissions.includes(permission) || config.permissions.includes('all');
};

// Check if user can perform action on target user
export const canActOnUser = (actorRole: UserRole, targetRole: UserRole): boolean => {
  const actorConfig = getRoleConfig(actorRole);
  const targetConfig = getRoleConfig(targetRole);
  
  // Higher hierarchy can act on lower hierarchy
  return actorConfig.hierarchy > targetConfig.hierarchy;
};

// Get CSS class for role badge
export const getRoleBadgeClass = (role: UserRole): string => {
  const config = getRoleConfig(role);
  return `inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.cssClass}`;
};

// Format user name with role
export const formatUserWithRole = (firstName: string, lastName: string, role: UserRole): string => {
  const roleConfig = getRoleConfig(role);
  return `Δρ. ${firstName} ${lastName} ${roleConfig.icon}`;
};

// Get all roles for dropdowns/selections
export const getAllRoles = (): RoleConfig[] => {
  return Object.values(ROLE_CONFIGS).sort((a, b) => a.hierarchy - b.hierarchy);
};

// Role validation for forms
export const isValidRole = (role: string): role is UserRole => {
  return Object.keys(ROLE_CONFIGS).includes(role);
};

// Get higher roles (for permission checks)
export const getHigherRoles = (currentRole: UserRole): UserRole[] => {
  const currentHierarchy = getRoleConfig(currentRole).hierarchy;
  return Object.values(ROLE_CONFIGS)
    .filter(config => config.hierarchy > currentHierarchy)
    .map(config => config.key);
};

// Check if role can moderate
export const canModerate = (role: UserRole): boolean => {
  return hasPermission(role, 'moderate_forum');
};

// Check if role can resolve cases
export const canResolveCases = (role: UserRole): boolean => {
  return hasPermission(role, 'resolve_cases');
};

// Check if role is admin
export const isAdmin = (role: UserRole): boolean => {
  return role === 'admin';
};

// Check if role is moderator or higher
export const isModerator = (role: UserRole): boolean => {
  return ['moderator', 'admin'].includes(role);
};

// Get gender-aware role label
export const getGenderedRoleLabel = (role: UserRole, gender?: 'male' | 'female'): string => {
  if (!gender) {
    return getRoleConfig(role).label;
  }
  return getGenderedUserRole(role as any, gender) || getRoleConfig(role).label;
};